/**
 * ESG SaaS Platform - Renewable Energy API Service
 *
 * API service for renewable energy data (RE100, GHG Protocol Scope 2).
 */

import { api } from './api';

// Types
export type RenewableSource = 'Solar PV' | 'Wind' | 'Hydro' | 'Biomass' | 'Geothermal' | 'Mixed Renewable';
export type ProcurementType = 'On-site Generation' | 'Power Purchase Agreement (PPA)' | 'Green Tariff' | 'Unbundled EACs';
export type CertificationType = 'Guarantee of Origin (GO)' | 'I-REC' | 'REC' | 'REGO' | 'None';

export interface RenewableEntryCreate {
  source: RenewableSource;
  procurement_type: ProcurementType;
  generation_mwh?: number;
  consumption_mwh: number;
  certification_type?: CertificationType;
  certificate_ids?: string[];
  facility_id?: string;  // Optional for organization-wide entries
  year: number;
  month: number;
  reporting_period?: string;  // Auto-generated from year/month by backend
  supplier?: string;
  contract_id?: string;
  notes?: string;
}

export interface RenewableEntry {
  id: string;
  source: string;
  procurement_type: string;
  generation_mwh?: number;
  consumption_mwh: number;
  certification_type: string;
  certificate_ids?: string[];
  facility_id: string;
  facility_name: string;
  reporting_period: string;
  supplier?: string;
  status: string;
  created_by: string;
  created_at: string;
  approved_by?: string;
  approved_at?: string;
}

export interface RenewableListResponse {
  entries: RenewableEntry[];
  total: number;
  limit: number;
  offset: number;
  total_generation_mwh: number;
  total_consumption_mwh: number;
  approved_count: number;
  pending_count: number;
}

export interface RenewableSummary {
  total_generation_mwh: number;
  total_consumption_mwh: number;
  total_grid_electricity_mwh: number;
  renewable_share_percentage: number;
  by_source: Record<string, number>;
  by_procurement: Record<string, number>;
  certificates_count: number;
  onsite_capacity_mw: number;
  approved_count: number;
  pending_count: number;
  completion_percentage: number;
}

export interface Certificate {
  id: string;
  type: string;
  source: string;
  mwh: number;
  issuing_body: string;
  valid_from: string;
  valid_to: string;
  status: string;
}

export interface RE100Progress {
  target_year: number;
  target_percentage: number;
  current_percentage: number;
  yearly_progress: { year: number; percentage: number }[];
  on_track: boolean;
  projected_2030: number;
}

// Query parameters
interface ListParams {
  period?: string;
  facility?: string;
  source?: RenewableSource;
  procurement_type?: ProcurementType;
  status?: string;
  limit?: number;
  offset?: number;
}

// Service functions
export const renewableService = {
  // CRUD Operations
  createEntry: (entry: RenewableEntryCreate) =>
    api.post<RenewableEntry>('/renewable/entries', entry),

  listEntries: (params: ListParams = {}) => {
    const query = new URLSearchParams();
    if (params.period) query.append('period', params.period);
    if (params.facility) query.append('facility', params.facility);
    if (params.source) query.append('source', params.source);
    if (params.procurement_type) query.append('procurement_type', params.procurement_type);
    if (params.status) query.append('status', params.status);
    if (params.limit) query.append('limit', params.limit.toString());
    if (params.offset) query.append('offset', params.offset.toString());
    const queryString = query.toString();
    return api.get<RenewableListResponse>(`/renewable/entries${queryString ? `?${queryString}` : ''}`);
  },

  getEntry: (entryId: string) =>
    api.get<RenewableEntry>(`/renewable/entries/${entryId}`),

  updateEntry: (entryId: string, entry: RenewableEntryCreate) =>
    api.put<{ id: string; updated: boolean }>(`/renewable/entries/${entryId}`, entry),

  deleteEntry: (entryId: string) =>
    api.delete<{ id: string; deleted: boolean }>(`/renewable/entries/${entryId}`),

  // Approval workflow
  approveEntry: (entryId: string, comment?: string) =>
    api.post<{ id: string; status: string }>(`/renewable/entries/${entryId}/approve`, { comment }),

  rejectEntry: (entryId: string, reason: string) =>
    api.post<{ id: string; status: string; reason: string }>(`/renewable/entries/${entryId}/reject`, { reason }),

  // Bulk upload
  bulkUpload: async (file: File, period: string) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.postFormData(`/renewable/bulk-upload?period=${encodeURIComponent(period)}`, formData);
  },

  // Analytics
  getSummary: (period?: string, facilityId?: string) => {
    const params = new URLSearchParams();
    if (period) params.append('period', period);
    if (facilityId) params.append('facility_id', facilityId);
    const query = params.toString() ? `?${params.toString()}` : '';
    return api.get<RenewableSummary>(`/renewable/summary${query}`);
  },

  getBySource: (period?: string) => {
    const query = period ? `?period=${period}` : '';
    return api.get<{ sources: { source: string; consumption_mwh: number; percentage: number }[] }>(
      `/renewable/by-source${query}`
    );
  },

  getByProcurement: (period?: string) => {
    const query = period ? `?period=${period}` : '';
    return api.get<{ procurement_types: { type: string; consumption_mwh: number; percentage: number; capacity_mw?: number }[] }>(
      `/renewable/by-procurement${query}`
    );
  },

  getCertificates: (period?: string, type?: CertificationType) => {
    const query = new URLSearchParams();
    if (period) query.append('period', period);
    if (type) query.append('type', type);
    const queryString = query.toString();
    return api.get<{ certificates: Certificate[]; total_mwh_covered: number }>(
      `/renewable/certificates${queryString ? `?${queryString}` : ''}`
    );
  },

  getRE100Progress: (year?: number) => {
    const query = year ? `?year=${year}` : '';
    return api.get<RE100Progress>(`/renewable/re100-progress${query}`);
  },

  /**
   * Export renewable energy data as CSV or Excel file (Admin only).
   */
  exportData: async (format: 'csv' | 'xlsx' = 'csv', period?: string): Promise<void> => {
    const query = new URLSearchParams({ format });
    if (period) query.append('period', period);

    const response = await api.getBlob(`/renewable/export?${query.toString()}`);
    const url = URL.createObjectURL(response.blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = response.filename || `renewable_export.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },
};
